import { memo as Memo, useMemo, useEffect } from 'react';

//* HOCs
import withDataContext from 'context/consumerHOC/DataConsumer';
import withUIContext from 'context/consumerHOC/UIConsumer';

//* Helper
import { changeCommaToSlash } from 'helpers/_functions';

//* Components
import Page from 'components/common/Page';
import Container from 'components/common/Container';
import FilesSection from 'components/common/FilesSection';
import IndividualHero from 'components/pages/WorkIndividual/HeroSection';
import ProjectInfo from 'components/pages/WorkIndividual/ProjectInfoSection';
import FullWidth from 'components/pages/WorkIndividual/FieldsSection/FullWidthSection';
import TextSection from 'components/pages/WorkIndividual/FieldsSection/TextSection';
import Slider from 'components/pages/WorkIndividual/FieldsSection/SliderSection';
import TextWithFile from 'components/pages/WorkIndividual/FieldsSection/TextWithFileSection';
import EqualTwo from 'components/pages/WorkIndividual/FieldsSection/EqualTwoSection';
import SwipeSection from 'components/pages/WorkIndividual/FieldsSection/SwipeSection';
import TestimonialSection from 'components/pages/WorkIndividual/FieldsSection/TestimonialSection';
import Roles from 'components/pages/WorkIndividual/RolesSection';

//* Style
import SingleProjectStyle from 'styles/pages/SingleProjectStyle';

const SingleProject = Memo(({ singleProject, pageParams, selectedLang, changeTheme }) => {
	//! Page Data
	const pageData = useMemo(() => singleProject?.[pageParams.selectedSlug]?.[selectedLang], [singleProject, pageParams, selectedLang]);

	useEffect(() => {
		pageData?.project && changeTheme(!pageData?.project?.is_dark);
	}, [pageData]);

	return (
		<Page
			className='singleProject'
			meta={pageData?.project?.meta}>
			{pageData && (
				<Container>
					<SingleProjectStyle>
						<IndividualHero banner={pageData.project.banner} />

						<ProjectInfo
							name={pageData.project.name}
							service={pageData.project.service && changeCommaToSlash(pageData.project.service)}
							industry={pageData.project.industry}
							client={pageData.project.client}
							year={pageData.project.year}
							websiteLink={pageData.project.website_link}
							overview={pageData.project.overview}
							challenge={pageData.project.challenge}
							solution={pageData.project.solution}
							awards={pageData.project.awards}
						/>

						{pageData.project.fields?.length > 0
							? pageData.project.fields.map((field, ind) => {
									if (field.type === 'full_width') {
										return (
											<FullWidth
												key={ind}
												file={field.file}
											/>
										);
									}

									if (field.type === 'text') {
										return (
											<TextSection
												key={ind}
												text={field.text}
											/>
										);
									}

									if (field.type === 'slider') {
										return (
											<Slider
												key={ind}
												slider_images={field.slider_images}
											/>
										);
									}

									if (field.type === 'textWithFile') {
										return (
											<TextWithFile
												key={ind}
												text={field.text}
												file={field.file}
											/>
										);
									}

									if (field.type === 'equalTwo') {
										return (
											<EqualTwo
												key={ind}
												left_file={field.left_file}
												right_file={field.right_file}
											/>
										);
									}

									if (field.type === 'swipe') {
										return (
											<SwipeSection
												key={ind}
												first_image={field.first_image}
												second_image={field.second_image}
											/>
										);
									}

									if (field.type === 'testimonial') {
										return (
											<TestimonialSection
												key={ind}
												text={field.text}
												image={field.image}
												author={field.author}
												position={field.position}
											/>
										);
									}
								})
							: null}

						{pageData.project.roles?.length > 0 ? <Roles roles={pageData.project.roles} /> : null}

						{pageData.relatedProjects.length > 0 ? (
							<FilesSection
								related
								viewAll
								typeAndClass={'landscape_left'}
								left_project={pageData.relatedProjects[0]}
								right_project={pageData.relatedProjects?.[1]}
							/>
						) : null}
					</SingleProjectStyle>
				</Container>
			)}
		</Page>
	);
});

export default withDataContext(withUIContext(SingleProject, ['changeTheme']), ['singleProject']);
